<script setup>
import { ref, watch } from "vue";
import { useProjectsPanel } from "../../composables/useProjectsPanel.js";
import { useInfiniteScroll } from "../../composables/useInfiniteScroll.js";
import Spinner from "../ui/Spinner.vue";

const props = defineProps({
  isActive: { type: Boolean, default: false }, // panel is mounted app-wide (v-show); only fetch once it's actually opened
});
const emit = defineEmits(["close"]);

const {
  currentProjectId, currentProject,
  projects, isLoadingProjects, isLoadingMoreProjects, hasMoreProjects, loadMoreProjects, ensureProjectsLoaded, openProject,
  starredDocs, otherDocs, isLoadingDocs, isLoadingMoreDocs, hasMoreDocs, loadMoreDocs,
  activate,
} = useProjectsPanel();

watch(() => props.isActive, (active) => { if (active) activate(); }, { immediate: true });

// One sentinel serves whichever list is currently showing (projects or docs)
const sentinelEl = ref(null);
useInfiniteScroll(sentinelEl, () => {
  if (currentProjectId.value) loadMoreDocs();
  else loadMoreProjects();
});

// Project switcher dropdown, shown from the header regardless of mode
const showSwitcher = ref(false);
function toggleSwitcher() {
  showSwitcher.value = !showSwitcher.value;
  if (showSwitcher.value) ensureProjectsLoaded();
}
function switchToProject(id) {
  showSwitcher.value = false;
  openProject(id);
}
</script>

<template>
  <div class="flex flex-col h-full w-72 bg-slate-100 dark:bg-slate-900 overflow-hidden" @click="showSwitcher = false">

    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shrink-0">
      <div class="relative flex-1 min-w-0" @click.stop>
        <button
          class="flex items-center gap-1.5 text-[15px] font-semibold text-slate-800 dark:text-slate-100 bg-transparent border-0 cursor-pointer p-0 hover:text-brand transition-colors max-w-full"
          @click="toggleSwitcher"
        >
          <span class="truncate">{{ currentProjectId ? (currentProject?.name ?? "Project") : "All Projects" }}</span>
          <svg class="shrink-0 text-slate-500" width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>

        <!-- Project switcher dropdown -->
        <div
          v-if="showSwitcher"
          class="absolute top-full left-0 mt-2 w-60 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1.5 z-50 overflow-hidden"
        >
          <p class="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 px-3 pt-1 pb-1.5 m-0">Projects</p>
          <div v-if="isLoadingProjects" class="flex justify-center py-4"><Spinner size="18px" /></div>
          <template v-else>
            <button
              v-for="p in projects"
              :key="p.id"
              :class="p.id === currentProjectId
                ? 'text-brand bg-orange-50 dark:bg-orange-950/30'
                : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'"
              class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors"
              @click="switchToProject(p.id)"
            >
              <svg class="shrink-0 text-slate-400" width="14" height="14" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8" />
                <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="currentColor" stroke-width="1.8" />
              </svg>
              <span class="truncate flex-1">{{ p.name }}</span>
            </button>
            <p v-if="!projects.length" class="text-xs text-slate-400 text-center py-3 m-0">No projects yet</p>
          </template>
        </div>
      </div>

      <!-- Close -->
      <button
        class="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-600 cursor-pointer transition-colors shrink-0 ml-2"
        @click="emit('close')"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <!-- ══ Inside a project: its documents ══════════════════════════════════ -->
    <template v-if="currentProjectId">
      <div v-if="isLoadingDocs" class="flex justify-center py-10"><Spinner size="22px" /></div>

      <div v-else class="flex-1 overflow-y-auto py-3 px-2">
        <!-- Starred section -->
        <template v-if="starredDocs.length">
          <p class="text-[11px] text-slate-400 dark:text-slate-500 px-2 pt-1 pb-1 m-0">Starred</p>
          <div
            v-for="doc in starredDocs"
            :key="'s-' + doc.id"
            class="flex items-center gap-2.5 px-2 py-2.5 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700/60 cursor-pointer group transition-colors"
          >
            <svg class="shrink-0 text-slate-500 dark:text-slate-400" width="20" height="22" viewBox="0 0 24 26" fill="none">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M14 2v6h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M8 13h8M8 17h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
            <span class="flex-1 text-[14px] font-medium text-slate-800 dark:text-slate-100 truncate min-w-0">{{ doc.name ?? "Untitled" }}</span>
            <button class="flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600 border-0 bg-transparent cursor-pointer shrink-0 transition-colors" @click.stop>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
              </svg>
            </button>
          </div>
          <div class="mx-2 my-2 h-px bg-slate-200 dark:bg-slate-700" />
        </template>

        <!-- Other documents -->
        <div class="flex items-center px-2 pt-1 pb-1 gap-1">
          <p class="text-[11px] text-slate-400 dark:text-slate-500 flex-1 m-0">All Documents</p>
          <button class="flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 border-0 bg-transparent cursor-pointer transition-colors" title="Filter">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M3 6h18M7 12h10M11 18h2" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
          <button class="flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 border-0 bg-transparent cursor-pointer transition-colors" title="Add document">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </div>

        <div
          v-for="doc in otherDocs"
          :key="doc.id"
          class="flex items-center gap-2.5 px-2 py-2.5 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700/60 cursor-pointer group transition-colors"
        >
          <svg class="shrink-0 text-slate-500 dark:text-slate-400" width="20" height="22" viewBox="0 0 24 26" fill="none">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M14 2v6h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M8 13h8M8 17h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <span class="flex-1 text-[14px] font-medium text-slate-800 dark:text-slate-100 truncate min-w-0">{{ doc.name ?? "Untitled" }}</span>
          <button class="flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600 border-0 bg-transparent cursor-pointer shrink-0 transition-colors" @click.stop>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
            </svg>
          </button>
        </div>

        <p v-if="!starredDocs.length && !otherDocs.length" class="text-xs text-slate-400 dark:text-slate-500 text-center py-6 px-4 m-0">No documents in this project</p>

        <!-- Infinite scroll sentinel -->
        <div ref="sentinelEl" class="h-px" />
        <div v-if="isLoadingMoreDocs" class="flex justify-center py-3"><Spinner size="16px" /></div>
      </div>
    </template>

    <!-- ══ Not inside a project: browse all projects ═════════════════════════ -->
    <template v-else>
      <div v-if="isLoadingProjects" class="flex justify-center py-10"><Spinner size="22px" /></div>

      <div v-else class="flex-1 overflow-y-auto py-3 px-2">
        <div
          v-for="p in projects"
          :key="p.id"
          class="flex items-center gap-2.5 px-2 py-2.5 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700/60 cursor-pointer transition-colors"
          @click="openProject(p.id)"
        >
          <svg class="shrink-0 text-slate-400" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8" />
            <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="currentColor" stroke-width="1.8" />
          </svg>
          <span class="flex-1 text-[14px] font-medium text-slate-800 dark:text-slate-100 truncate min-w-0">{{ p.name }}</span>
        </div>

        <p v-if="!projects.length" class="text-xs text-slate-400 dark:text-slate-500 text-center py-6 px-4 m-0">No projects yet</p>

        <!-- Infinite scroll sentinel -->
        <div ref="sentinelEl" class="h-px" />
        <div v-if="isLoadingMoreProjects" class="flex justify-center py-3"><Spinner size="16px" /></div>
      </div>
    </template>
  </div>
</template>
